import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import {Camera, Map, Activity, Zap, Sprout, LifeBuoy, ArrowRight, CheckCircle2} from 'lucide-react';
import { motion } from 'framer-motion';

// Service images - professional drone photography
const images = {
  hero: "https://static.lumi.new/6e/6e26558f7d1aeda12fc4f8b0124e0be2.png",
  photo: "https://static.lumi.new/a3/a31b91225f938abbf513644e077e3679.png",
  event: "https://static.lumi.new/bc/bcc875b66168b9451925519f3bf94b51.png",
  map: "https://static.lumi.new/dc/dcef7e89dc6a74d46fa22769604f1da0.png",
  inspect: "https://static.lumi.new/68/68bf6955d7e5c69569b42f59a651e81e.png",
  agri: "https://static.lumi.new/d6/d6b920a40239b1ba26f88b9a6a1f1057.png",
  rescue: "https://static.lumi.new/52/526d1fff35441fd4f124ab47977ea5ca.png",
};

const Home = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const services = [
    {
      id: 'photo',
      icon: <Camera className="h-6 w-6" />,
      image: images.photo,
      price: '$250 – $300'
    },
    {
      id: 'event',
      icon: <Activity className="h-6 w-6" />,
      image: images.event,
      price: '$250 – $300'
    },
    {
      id: 'map',
      icon: <Map className="h-6 w-6" />,
      image: images.map,
      price: '$300 – $400'
    },
    {
      id: 'inspect',
      icon: <Zap className="h-6 w-6" />,
      image: images.inspect,
      price: '$350 – $500'
    },
    {
      id: 'agri',
      icon: <Sprout className="h-6 w-6" />,
      image: images.agri,
      price: '$250 – $350'
    },
    {
      id: 'rescue',
      icon: <LifeBuoy className="h-6 w-6" />,
      image: images.rescue,
      price: '$400 – $500'
    }
  ];

  return (
    <div className="bg-slate-50">
      {/* Hero Section */}
      <section id="home" className="relative h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={images.hero} 
            alt="Drone over Ghana" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/50 to-slate-900/90" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-extrabold text-white tracking-tight mb-6"
          >
            {t('hero.title')}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-gray-200 mb-10 max-w-3xl mx-auto"
          >
            {t('hero.subtitle')}
          </motion.p>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('services.title')}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t('services.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div 
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group bg-slate-50 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100"
              >
                <div className="h-48 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={t(`service.${service.id}.title`)} 
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="w-12 h-12 bg-sky-100 rounded-lg flex items-center justify-center mb-4 text-sky-600">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{t(`service.${service.id}.title`)}</h3>
                  <p className="text-gray-600 mb-4">{t(`service.${service.id}.desc`)}</p>
                  <div className="pt-4 border-t border-slate-200 flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-500">{t('pricing.starting_from')}</span>
                    <span className="text-lg font-bold text-sky-600">{service.price}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button
              onClick={() => navigate('/request-service')}
              className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-semibold px-8 py-4 rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl"
            >
              {t('hero.cta')}
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </section>


    </div>
  );
};

export default Home;
